// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BaseInput, BaseModal, BaseSelect, toast} from "@/components";
import {usePaykuTransactionStatus, usePostPaykuDeposit} from "../../../hooks/wallet/usePostPaykuDeposit";
import useCurrencyStore from "../../../store/useCurrency.store";
import BaseButton from "../../Button";
import {MIN_PAYKU_DEPOSIT, paykuDepositSchema} from "./paykuDeposit.schema";

const paykuAmountInputCls =
  "[&_input[type=number]::-webkit-outer-spin-button]:appearance-none [&_input[type=number]::-webkit-outer-spin-button]:m-0 [&_input[type=number]::-webkit-inner-spin-button]:appearance-none [&_input[type=number]::-webkit-inner-spin-button]:m-0 [&_input[type=number]]:[-moz-appearance:textfield]";

const PaykuPaymentModal = ({visible, onClose, paymentUrl, orderId, amount, handleModalClose}) => {
  const {data: transactionStatusResponse, error, isLoading} = usePaykuTransactionStatus(orderId, visible);
  const {t} = useTranslation();

  const transactionData = transactionStatusResponse?.data?.data;
  const actualStatus = transactionData?.status;

  useEffect(() => {
    if (actualStatus === "approved" || actualStatus === "completed") {
      toast.success(t("depositInstructionsPaykuPaymentCompleted"));
      const id = setTimeout(() => onClose(), 3000);
      return () => clearTimeout(id);
    }
    if (actualStatus === "rejected") {
      toast.error(t("depositInstructionsPaykuPaymentRejected"));
    }
    if (actualStatus === "expired") {
      toast.warning(t("depositInstructionsPaykuPaymentExpired"));
    }
    if (error) {
      // silent
    }
  }, [actualStatus, onClose, error, t]);

  const handleOpenPayment = () => {
    if (paymentUrl) window.open(paymentUrl, "_blank");
  };

  const statusColor =
    actualStatus === "approved" || actualStatus === "completed"
      ? "var(--color-success-text)"
      : actualStatus === "rejected"
        ? "var(--color-danger-text)"
        : "var(--color-warning-text)";

  return (
    <BaseModal
      open={visible}
      onClose={onClose}
      title={t("depositInstructionsPaykuCompletePayment")}
      size="md"
    >
      <div style={{textAlign: "center", padding: "var(--space-5)"}}>
        <div style={{marginBottom: "var(--space-5)"}}>
          <h3 style={{marginBottom: "var(--space-2-5)"}}>
            {t("depositInstructionsPaykuAmount")}: ${amount} CLP
          </h3>
          <p style={{color: "var(--color-text-secondary)", marginBottom: "var(--space-5)"}}>
            {t("depositInstructionsPaykuOptions")}
          </p>
        </div>

        <div style={{marginBottom: "var(--space-5)", width: "100%"}}>
          <BaseButton
            variant="primary"
            onClick={() => {
              handleOpenPayment();
              handleModalClose();
            }}
            title={t("depositInstructionsPaykuOpenLink")}
          />
        </div>

        <div style={{marginBottom: "var(--space-3-75)"}}>
          <h4>{t("depositInstructionsPaykuStatus")}</h4>
          <p style={{color: statusColor}}>
            {isLoading
              ? t("depositInstructionsPaykuCheckingStatus")
              : actualStatus
                ? t(`depositInstructionsPayku${actualStatus}`)
                : t("depositInstructionsPaykuPending")}
          </p>
        </div>

        <p style={{fontSize: "var(--font-body)", color: "var(--color-text-secondary)"}}>
          {t("depositInstructionsPaykuInstructions")}
        </p>
      </div>
    </BaseModal>
  );
};

PaykuPaymentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentUrl: PropTypes.string,
  orderId: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleModalClose: PropTypes.func,
};

const PaykuOptions = ({onBack}) => {
  const {t} = useTranslation();
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(99);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);
  const {mutate: createPaykuDeposit, isPending, isError, error} = usePostPaykuDeposit();

  const CHILE_PAYMENT_METHODS = [
    {
      id: 99,
      name: t("depositInstructionsPaykuAllMethods"),
      description: t("depositInstructionsPaykuAllMethodsDescription"),
    },
    {id: 1, name: "Webpay", description: t("depositInstructionsPaykuWebpayDescription")},
    {id: 4, name: "Etpay", description: ""},
    {id: 6, name: "Pago46", description: ""},
    {id: 8, name: "Hites", description: ""},
    {id: 9, name: "Mach", description: ""},
    {id: 11, name: "Khipu", description: ""},
    {id: 15, name: "ServiFacil", description: ""},
    {id: 19, name: "Fintoc", description: ""},
    {
      id: 100,
      name: t("depositInstructionsPaykuWebpayPlus1_3"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
    {
      id: 101,
      name: t("depositInstructionsPaykuWebpayPlus4_6"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
    {
      id: 102,
      name: t("depositInstructionsPaykuWebpayPlus7_12"),
      description: t("depositInstructionsPaykuWebpayPlusDesc"),
    },
  ];

  const isValidCurrency = selectedCurrency?.id === "CLP";

  if (!isValidCurrency) {
    return (
      <div style={{textAlign: "center", padding: "2rem"}}>
        <h3>{t("depositInstructionsPaykuUnsupportedCurrency")}</h3>
        <p>{t("depositInstructionsPaykuOnlyCLP")}</p>
        <div style={{marginTop: "1rem"}}>
          <BaseButton
            onClick={onBack}
            variant="secondary"
            title={t("depositInstructionsBack")}
          />
        </div>
      </div>
    );
  }

  const handleSubmit = e => {
    e.preventDefault();
    const result = paykuDepositSchema.safeParse({amount, paymentMethod});
    if (!result.success) {
      for (const issue of result.error.issues) {
        const message = issue.message.includes(String(MIN_PAYKU_DEPOSIT))
          ? t("depositInstructionsPaykuMinAmountCustom", {min: MIN_PAYKU_DEPOSIT})
          : issue.message === "Selecciona un método de pago"
            ? t("depositInstructionsPaykuSelectMethod")
            : issue.message;
        toast.error(message);
      }
      return;
    }

    createPaykuDeposit(
      {amount: result.data.amount, paymentMethod: result.data.paymentMethod, currency: "CLP"},
      {
        onSuccess: response => {
          if (response.data?.status === 1 && response.data?.data) {
            setPaymentData({
              orderId: response.data.data.orderId,
              paymentUrl: response.data.data.payment_url,
              amount,
            });
            setIsModalVisible(true);
          } else {
            toast.error(response.data?.message || t("depositInstructionsPaykuError"));
          }
        },
        onError: err => {
          if (Number(amount) < MIN_PAYKU_DEPOSIT) {
            toast.error(t("depositInstructionsPaykuMinAmountCustom", {min: MIN_PAYKU_DEPOSIT}));
          } else if (err?.response?.status === 500) {
            toast.error(t("depositInstructionsPaykuFailedTransactionMin", {min: MIN_PAYKU_DEPOSIT}));
          } else {
            toast.error(err.message || t("depositInstructionsPaykuError"));
          }
        },
      }
    );
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setPaymentData(null);
    setAmount("");
    setPaymentMethod(99);
  };

  const methodOptions = CHILE_PAYMENT_METHODS.map(m => ({
    value: m.id,
    label: m.description ? `${m.name} — ${m.description}` : m.name,
  }));

  return (
    <div>
      <div style={{marginBottom: "2rem"}}>
        <h3>{t("depositInstructionsPaykuTitle")}</h3>
        <p style={{color: "var(--color-text-secondary)", marginBottom: "1rem"}}>
          {t("depositInstructionsPaykuDescription")}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{maxWidth: 400, margin: "0 auto"}}
        noValidate
      >
        <div
          className={paykuAmountInputCls}
          style={{marginBottom: "var(--space-5-5)"}}
        >
          <label
            style={{fontWeight: 500, fontSize: "var(--font-body-lg)", display: "block", marginBottom: "var(--space-2)"}}
          >
            {t("depositInstructionsPaykuAmount")} <span style={{color: "var(--color-danger-text)"}}>*</span>
          </label>
          <BaseInput
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder={t("depositInstructionsPaykuEnterAmount")}
            rightIcon={
              <span style={{paddingRight: "var(--space-2-5)", color: "var(--color-text-secondary)"}}>CLP</span>
            }
          />
          <div
            style={{
              fontSize: "var(--font-body)",
              color: "var(--color-text-secondary)",
              marginTop: "var(--space-2-5)",
              fontStyle: "italic",
            }}
          >
            {t("depositInstructionsPaykuMinAmount", {min: MIN_PAYKU_DEPOSIT})}
          </div>
        </div>

        <div style={{marginBottom: "var(--space-5-5)"}}>
          <label
            style={{fontWeight: 500, fontSize: "var(--font-body-lg)", display: "block", marginBottom: "var(--space-2)"}}
          >
            {t("depositInstructionsPaykuPaymentMethod")} <span style={{color: "var(--color-danger-text)"}}>*</span>
          </label>
          <BaseSelect
            value={paymentMethod}
            onChange={e => setPaymentMethod(Number(e.target.value))}
            placeholder={t("depositInstructionsPaykuSelectMethod")}
            options={methodOptions}
          />
        </div>

        <div style={{display: "flex", gap: "0.5rem", marginTop: "2rem", justifyContent: "center"}}>
          <BaseButton
            onClick={onBack}
            variant="secondary"
            title={t("depositInstructionsBack")}
            classContainer=""
            classBtn="w-full"
          />
          <BaseButton
            type="submit"
            variant="primary"
            isLoading={isPending}
            title={t("depositInstructionsPaykuCreatePayment")}
            classContainer=""
            classBtn="w-full"
          />
        </div>

        {isError && error && (
          <div style={{marginTop: "1rem", color: "var(--color-danger-text)", textAlign: "center"}}>
            {error.message || t("depositInstructionsPaykuError")}
          </div>
        )}
      </form>

      {paymentData && (
        <PaykuPaymentModal
          visible={isModalVisible}
          onClose={handleModalClose}
          paymentUrl={paymentData.paymentUrl}
          orderId={paymentData.orderId}
          amount={paymentData.amount}
          handleModalClose={handleModalClose}
        />
      )}
    </div>
  );
};

PaykuOptions.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default PaykuOptions;
